import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Workspaces
export const workspaceAPI = {
  list: () => api.get('/workspaces'),
  get: (id) => api.get(`/workspaces/${id}`),
  create: (data) => api.post('/workspaces', data),
};

// Users
export const userAPI = {
  list: () => api.get('/users'),
  addToWorkspace: (wsId, userId, role) => api.post(`/workspaces/${wsId}/users`, { user_id: userId, role }),
};

// Email Campaigns
export const emailCampaignAPI = {
  create: (data) => api.post('/email-campaigns', data),
  list: () => api.get('/email-campaigns'),
  get: (id) => api.get(`/email-campaigns/${id}`),
  start: (id) => api.post(`/email-campaigns/${id}/start`),
  startBulk: (id) => api.post(`/email-campaigns/${id}/start-bulk`),
  stop: (id) => api.post(`/email-campaigns/${id}/stop`),
  pause: (id) => api.post(`/email-campaigns/${id}/pause`),
  restart: (id) => api.post(`/email-campaigns/${id}/restart`),
  retryFailed: (id) => api.post(`/email-campaigns/${id}/retry-failed`),
  delete: (id) => api.delete(`/email-campaigns/${id}`),
  analytics: (id) => api.get(`/email-campaigns/${id}/analytics`),
  queueStats: (id) => api.get(`/email-campaigns/${id}/queue-stats`),
  contactTimeline: (id, email) => api.get(`/email-campaigns/${id}/timeline/${email}`),
  clone: (id, name) => api.post(`/email-campaigns/${id}/clone`, { name }),
  smartResend: (id, data) => api.post(`/email-campaigns/${id}/smart-resend`, data),
  segments: (id) => api.get(`/email-campaigns/${id}/segments`),
  exportCSV: (id) => `${API_BASE_URL}/api/email-campaigns/${id}/export/csv`,
  exportXLSX: (id) => `${API_BASE_URL}/api/email-campaigns/${id}/export/xlsx`,
  generatePDF: (id) => api.post(`/email-campaigns/${id}/generate-pdf`),
  downloadPDF: (id) => `${API_BASE_URL}/api/email-campaigns/${id}/pdf`,
  preCheck: (id) => api.post(`/email-campaigns/${id}/pre-check`),
  startDryRun: (id) => api.post(`/email-campaigns/${id}/start-dry-run`),
};

// Admin
export const adminAPI = {
  status: () => api.get('/admin/status'),
};

// Sender Domains
export const senderDomainAPI = {
  list: () => api.get('/sender-domains'),
  add: (data) => api.post('/sender-domains', data),
  get: (id) => api.get(`/sender-domains/${id}`),
  update: (id, data) => api.put(`/sender-domains/${id}`, data),
  delete: (id) => api.delete(`/sender-domains/${id}`),
  verify: (id) => api.post(`/sender-domains/${id}/verify`),
  pause: (id) => api.post(`/sender-domains/${id}/pause`),
  resume: (id) => api.post(`/sender-domains/${id}/resume`),
  dns: (id) => api.get(`/sender-domains/${id}/dns`),
  capacity: () => api.get('/sender-domains/capacity/total'),
  advanceWarmup: () => api.post('/sender-domains/warmup/advance'),
  autoProtect: () => api.post('/sender-domains/auto-protect'),
  byDomain: (domain) => api.get(`/sender-domains/by-domain/${domain}`),
};

// Reputation
export const reputationAPI = {
  calculate: (id) => api.post(`/reputation/calculate/${id}`),
  autoProtect: () => api.post('/reputation/auto-protect'),
  auditLogs: (limit) => api.get('/reputation/audit-logs', { params: { limit } }),
};

// Content Variation
export const variationAPI = {
  generate: (subject, body, count) => api.post('/email/ai/variations', { subject, body, count }),
};

// Orchestrator
export const orchestratorAPI = {
  distribute: (emails, mode) => api.post('/orchestrator/distribute', { emails, mode }),
};

// Global Suppression
export const globalSuppressionAPI = {
  add: (email, reason) => api.post('/global-suppression/add', { email, reason }),
  stats: () => api.get('/global-suppression/stats'),
  sync: () => api.post('/global-suppression/sync-events'),
  check: (emails) => api.post('/global-suppression/check', { emails }),
};

// Enterprise Dashboard
export const enterpriseAPI = {
  dashboard: () => api.get('/enterprise/dashboard'),
};

// Tracking
export const trackingAPI = {
  stats: (campaignId) => api.get(`/tracking/stats/${campaignId}`),
};

// Deliverability AI
export const deliverabilityAIAPI = {
  analyze: (campaignId) => api.post(`/ai/deliverability/analyze/${campaignId}`),
  domain: (domainId) => api.get(`/ai/deliverability/domain/${domainId}`),
};

// Infra
export const infraAPI = {
  dashboard: () => api.get('/infra/dashboard'),
  addNode: (data) => api.post('/infra/nodes', data),
  assignDomain: (nodeId, domainId) => api.post('/infra/assign-domain', { node_id: nodeId, domain_id: domainId }),
  pauseNode: (id) => api.post(`/infra/node/${id}/pause`),
  resumeNode: (id) => api.post(`/infra/node/${id}/resume`),
};

// Stealth
export const stealthAPI = {
  modes: () => api.get('/stealth/modes'),
  mode: (m) => api.get(`/stealth/mode/${m}`),
  validate: (mode) => api.post('/stealth/validate', { mode }),
};

// Security
export const securityAPI = {
  checkCampaign: (id) => api.post(`/security/check-campaign/${id}`),
  logs: (limit) => api.get('/security/logs', { params: { limit } }),
  abuseDetect: () => api.get('/security/abuse-detect'),
  advancedAbuse: () => api.get('/security/abuse/detect'),
  auditLogs: (limit) => api.get('/security/audit-logs', { params: { limit } }),
  createKey: (name) => api.post('/security/api-keys/create', { name }),
  rotateKey: (key) => api.post('/security/api-keys/rotate', { old_key: key }),
  revokeKey: (key) => api.post('/security/api-keys/revoke', { key }),
  keyStatus: () => api.get('/security/api-keys/status'),
  listKeys: () => api.get('/security/api-keys/list'),
};

// Operations Center
export const opsAPI = {
  center: () => api.get('/ops/center'),
  alerts: () => api.get('/ops/alerts'),
  queueStats: () => api.get('/ops/queue/stats'),
  drainQueue: () => api.post('/ops/queue/drain'),
  killSwitch: () => api.post('/ops/emergency/kill-switch'),
  freezeCampaign: (id) => api.post(`/ops/emergency/freeze-campaign/${id}`),
  failoverNode: (id) => api.post(`/ops/failover/node/${id}`),
  providerTimeout: (p) => api.post('/ops/failover/provider-timeout', { provider: p }),
  adjustWarmup: () => api.post('/ops/warmup/adjust'),
  createBackup: () => api.post('/ops/backup/create'),
  listBackups: () => api.get('/ops/backup/list'),
  restoreBackup: (id) => api.post(`/ops/backup/restore/${id}`),
};

// Global Round Robin Campaign
export const globalCampaignAPI = {
  create: (data) => api.post('/global-campaign/create', data),
  start: (id) => api.post(`/global-campaign/${id}/start`),
  stop: (id) => api.post(`/global-campaign/${id}/stop`),
  get: (id) => api.get(`/global-campaign/${id}`),
  list: () => api.get('/global-campaigns'),
  domainPerformance: () => api.get('/domain-performance'),
  domainAudit: () => api.get('/domain-audit'),
  schedulerLogs: () => api.get('/scheduler/logs'),
};

// Quarantine
export const quarantineAPI = {
  check: () => api.post('/quarantine/check'),
  list: () => api.get('/quarantine/list'),
  release: (id, note) => api.post(`/quarantine/release/${id}`, { note }),
};

// Reports
export const reportsAPI = {
  campaign: (id) => api.get(`/reports/campaign/${id}`),
  deliverability: () => api.get('/reports/deliverability'),
  campaignCSV: (id) => `${API_BASE_URL}/api/reports/campaign/${id}/export/csv`,
};

// Sprint 5: Hardening
export const hardeningAPI = {
  alerts: () => api.get('/alerts/deliverability'),
  recoveryCheck: () => api.post('/recovery/check'),
  reputationSnapshot: () => api.post('/reputation/snapshot'),
  reputationSummary: () => api.get('/reputation/summary'),
  reputationHistory: (domain, days) => api.get(`/reputation/history/${domain}`, { params: { days } }),
  productionMonitor: () => api.get('/monitor/production'),
  seedLists: () => api.get('/seed-test/lists'),
  createSeedList: (name, emails) => api.post('/seed-test/lists', { name, emails }),
};

// Template Library
export const templateLibAPI = {
  save: (data) => api.post('/templates/email/save', data),
  list: () => api.get('/templates/email/list'),
  get: (id) => api.get(`/templates/email/${id}`),
  update: (id, data) => api.put(`/templates/email/${id}`, data),
  delete: (id) => api.delete(`/templates/email/${id}`),
  aiVariations: (subject, body, count) => api.post('/templates/ai/variations', { subject, body, count }),
};

// Mail Lists
export const mailListAPI = {
  list: () => api.get('/mail-lists'),
  get: (id) => api.get(`/mail-lists/${id}`),
  delete: (id) => api.delete(`/mail-lists/${id}`),
  importCSV: (file, listName) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('list_name', listName);
    return api.post('/mail-lists/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  createManual: (name, emails) => api.post('/mail-lists/manual', { name, emails }),
};

// Auto Scaling
export const scalingAPI = {
  dashboard: () => api.get('/scaling/dashboard'),
  applyHealth: () => api.post('/scaling/apply-health'),
  projection: (days) => api.get('/scaling/projection', { params: { days } }),
};

// Provider Intelligence 2.0
export const providerIntelAPI = {
  dashboard: () => api.get('/provider-intelligence/dashboard'),
  provider: (p) => api.get(`/provider-intelligence/${p}`),
  build: () => api.post('/provider-intelligence/build'),
  checkGmail: () => api.post('/provider-intelligence/check-gmail'),
};

// Deliverability & Warmup
export const deliverabilityAPI = {
  health: (campaignId) => api.get('/deliverability/health', { params: { campaign_id: campaignId } }),
  warmup: () => api.get('/warmup/status'),
};

// AI Email
export const aiEmailAPI = {
  generateSubjects: (content, tone, count) => api.post('/email/ai/generate-subjects', { content, tone, count }),
  spamCheck: (subject, body) => api.post('/email/ai/spam-check', { subject, body }),
  predict: (subject, body, emailCount) => api.post('/email/ai/predict', { subject, body, email_count: emailCount }),
  optimizeContent: (bodyHtml, goal) => api.post('/email/ai/optimize-content', { body_html: bodyHtml, goal }),
  recommendations: (campaignId) => api.get('/email/ai/recommendations', { params: { campaign_id: campaignId } }),
  aggregate: () => api.post('/email/ai/aggregate'),
  createABTest: (campaignId, subjects, pct) => api.post(`/email-campaigns/${campaignId}/ab-test`, { subjects, test_percentage: pct }),
  getABTest: (campaignId) => api.get(`/email-campaigns/${campaignId}/ab-test`),
  selectWinner: (campaignId) => api.post(`/email-campaigns/${campaignId}/ab-test/select-winner`),
};

// Send Time Optimization
export const sendTimeAPI = {
  buildProfiles: () => api.post('/sendtime/build-profiles'),
  stats: () => api.get('/sendtime/stats'),
  contact: (email) => api.get(`/sendtime/contact/${email}`),
};

// Provider Snapshot (email provider health)
export const providerSnapshotAPI = {
  intelligence: () => api.get('/providers/intelligence'),
  snapshot: () => api.post('/providers/snapshot'),
};

// Compliance
export const complianceAPI = {
  suppression: (page, limit) => api.get('/compliance/suppression', { params: { page, limit } }),
  stats: () => api.get('/compliance/suppression/stats'),
  suppress: (email, reason) => api.post('/compliance/suppress', { email, reason }),
  unsuppress: (email) => api.delete(`/compliance/suppress/${email}`),
  autoSuppress: () => api.post('/compliance/auto-suppress'),
  checkDuplicates: (emails) => api.post('/compliance/check-duplicates', { emails }),
};

// Email Templates
export const emailTemplateAPI = {
  save: (data) => api.post('/email-templates', data),
  list: (workspaceId) => api.get('/email-templates', { params: { workspace_id: workspaceId } }),
  versions: (id) => api.get(`/email-templates/${id}/versions`),
  restore: (id, version) => api.post(`/email-templates/${id}/restore/${version}`),
};

// Email Cleaner
export const emailCleanerAPI = {
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/email-cleaner/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  jobs: () => api.get('/email-cleaner/jobs'),
  job: (id) => api.get(`/email-cleaner/jobs/${id}`),
  results: (id) => api.get(`/email-cleaner/results/${id}`),
  exportURL: (id, type, token) => `${API_BASE_URL}/api/email-cleaner/export/${id}/${type}?token=${token}`,
  typoList: (id) => api.get(`/email-cleaner/typo-list/${id}`),
  typoApprove: (id, data) => api.post(`/email-cleaner/typo-approve/${id}`, data),
  typoApproveAll: (id) => api.post(`/email-cleaner/typo-approve-all/${id}`),
  typoAudit: (id) => api.get(`/email-cleaner/typo-audit/${id}`),
  toCampaign: (id, data) => api.post(`/email-cleaner/to-campaign/${id}`, data),
};

// Admin Email Exports
export const emailExportAPI = {
  start: () => api.post('/admin/email-exports/start'),
  status: (jobId) => api.get(`/admin/email-exports/status/${jobId}`),
  jobs: () => api.get('/admin/email-exports/jobs'),
};

export default api;
